import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import useOverlayDismiss from './useOverlayDismiss';

// Escape sobre uno de estos NO cierra el modal: cada uno tiene su propio desplegable
// nativo (sugerencias de autofill en INPUT/TEXTAREA, la lista de opciones en SELECT) que
// Escape debe cerrar primero. Antes SELECT no estaba, así que cerrar su lista cerraba el
// modal entero. El botón X y «Cancelar» siguen operativos para el cierre con teclado.
const FORM_CONTROLS = ['INPUT', 'TEXTAREA', 'SELECT'];

const sizeMaxWidth = {
  /** Compact dialogs that match a fixed Figma width (e.g. Gestión de saldo ≈ 423px) */
  balance: 'max-w-[423px]',
  sm: 'max-w-md',
  md: 'max-w-2xl',
  large: 'max-w-4xl',
  xl: 'max-w-6xl',
  /** Wide forms (socios, propietarios) — horizontal dense layouts */
  wide: 'max-w-7xl',
};

/**
 * Unified modal shell aligned with Figma UI Design – WeedAgent (shadcn-style).
 * Uses .wa-modal-* from src/styles/figma-forms.css
 */
export default function CustomModal({
  active,
  handleModal,
  title,
  children,
  footer = null,
  headerExtra = null,
  size = 'md',
  titleId = 'modal-title',
}) {
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);
  const handleModalRef = useRef(handleModal);

  useEffect(() => {
    handleModalRef.current = handleModal;
  }, [handleModal]);

  useEffect(() => {
    if (active) {
      previousFocusRef.current = document.activeElement;
      const focusTimeout = setTimeout(() => {
        const activeElement = document.activeElement;
        if (
          activeElement?.tagName !== 'INPUT' &&
          activeElement?.tagName !== 'TEXTAREA' &&
          activeElement?.tagName !== 'BUTTON' &&
          !activeElement?.closest('[role="dialog"]')
        ) {
          modalRef.current?.focus();
        }
      }, 150);

      const handleEscape = (e) => {
        if (e.key !== 'Escape') return;
        const el = document.activeElement;
        const inFormControl = FORM_CONTROLS.includes(el?.tagName) || el?.isContentEditable;
        if (!inFormControl) handleModalRef.current?.();
      };

      document.addEventListener('keydown', handleEscape);
      return () => {
        clearTimeout(focusTimeout);
        document.removeEventListener('keydown', handleEscape);
        previousFocusRef.current?.focus();
      };
    }
  }, [active]);

  // Al nivel del componente, antes de cualquier return: un hook no puede llamarse condicionalmente.
  const overlayDismiss = useOverlayDismiss(() => handleModalRef.current?.());

  if (!active) return null;

  const maxW = sizeMaxWidth[size] || sizeMaxWidth.md;

  const modalContent = (
    <div
      ref={modalRef}
      id="custom-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      className="wa-modal-overlay"
      {...overlayDismiss}
    >
      {/* El panel ya NO necesita stopPropagation: el overlay solo cierra si el gesto entero
          (mousedown+mouseup+click) ocurre sobre él, no si un click burbujea desde dentro. */}
      <div className={`wa-modal-panel ${maxW}`}>
        <div className={`wa-modal-header${headerExtra ? ' wa-modal-header--stacked' : ''}`}>
          <div className="wa-modal-header-row">
            <h3 id={titleId} className="wa-modal-title">
              {title}
            </h3>
            <button
              onClick={() => handleModalRef.current?.()}
              type="button"
              className="wa-modal-close"
              aria-label="Cerrar modal"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
          {headerExtra}
        </div>

        <div className="wa-modal-scroll">{children}</div>

        {footer != null && footer !== false && <div className="wa-modal-footer">{footer}</div>}
      </div>
    </div>
  );

  // Portal a document.body: evita que un ancestro con transform/filter (iOS Safari)
  // atrape el overlay 'position: fixed' y deje el sidebar por encima del modal.
  return typeof document !== 'undefined'
    ? createPortal(modalContent, document.body)
    : modalContent;
}
